// Instagram images (feed post and story) for an event, drawn on a canvas in
// the promoter's browser: the event wallpaper as background; centred on it
// the event logo, its name, dates, location and a "book your space" button;
// the there2b wordmark at the bottom. In Portuguese or English. No QR code:
// that's its own download in the share kit.
//
// The wallpaper and logo come through the API (/events/:id/share-media/…)
// rather than straight from S3: S3 sends no CORS headers, and a canvas that
// has drawn a cross-origin image can't be exported.

import api from './axiosConfig';
import { getEventDateRange } from './eventDateRange';
import wordmarkSrc from 'assets/images/there2b-wordmark.png';

export const SHARE_FORMATS = {
  story: { key: 'story', width: 1080, height: 1920 },
  post: { key: 'post', width: 1080, height: 1350 },
};

const BRAND = '#FFBD59';
const INK = '#111111';
const FONT = 'Manrope, Inter, system-ui, -apple-system, sans-serif';

// ── text helpers ────────────────────────────────────────────────────────────

const MONTHS = {
  pt: ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'],
  en: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
};

/** "23 jul – 22 out 2026", "30 Dec 2026 – 2 Jan 2027", "5 mai 2026". */
export const formatShareDate = (rawDate, lang = 'pt') => {
  const range = getEventDateRange(rawDate);
  if (!range) return '';
  const months = MONTHS[lang] || MONTHS.pt;
  const parse = (s) => {
    const [y, m, d] = s.split('-').map(Number);
    return { y, m, d };
  };
  const a = parse(range.startDate);
  const b = parse(range.endDate);
  const day = (x) => `${x.d} ${months[x.m - 1]}`;
  if (range.startDate === range.endDate) return `${day(a)} ${a.y}`;
  const from = a.y === b.y ? day(a) : `${day(a)} ${a.y}`;
  return `${from} – ${day(b)} ${b.y}`;
};

export const shareLocation = (event) =>
  event?.city || event?.location || event?.formattedAddress || '';

const font = (weight, size) => `${weight} ${size}px ${FONT}`;

// Greedy word wrap; a word wider than the line is broken by characters.
const wrapText = (ctx, text, maxWidth) => {
  const lines = [];
  let line = '';
  const push = (word) => {
    const next = line ? `${line} ${word}` : word;
    if (ctx.measureText(next).width <= maxWidth) {
      line = next;
      return;
    }
    if (line) lines.push(line);
    line = '';
    if (ctx.measureText(word).width <= maxWidth) {
      line = word;
      return;
    }
    let chunk = '';
    for (const ch of word) {
      if (ctx.measureText(chunk + ch).width > maxWidth && chunk) {
        lines.push(chunk);
        chunk = ch;
      } else {
        chunk += ch;
      }
    }
    line = chunk;
  };
  String(text || '')
    .split(/\s+/)
    .filter(Boolean)
    .forEach(push);
  if (line) lines.push(line);
  return lines;
};

const ellipsize = (ctx, text, maxWidth) => {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let s = text;
  while (s.length > 1 && ctx.measureText(`${s}…`).width > maxWidth) s = s.slice(0, -1);
  return `${s.trimEnd()}…`;
};

// Largest size (step 4px) at which the text fits the box.
const fitText = (ctx, text, { maxWidth, maxHeight, maxSize, minSize, maxLines, weight }) => {
  for (let size = maxSize; size >= minSize; size -= 4) {
    ctx.font = font(weight, size);
    const lines = wrapText(ctx, text, maxWidth);
    const lineHeight = Math.round(size * 1.08);
    if (lines.length <= maxLines && lines.length * lineHeight <= maxHeight) {
      return { size, lines, lineHeight };
    }
  }
  ctx.font = font(weight, minSize);
  const lineHeight = Math.round(minSize * 1.08);
  const lines = wrapText(ctx, text, maxWidth).slice(0, maxLines);
  const last = lines.length - 1;
  if (last >= 0) lines[last] = ellipsize(ctx, lines[last], maxWidth);
  return { size: minSize, lines, lineHeight };
};

// ── drawing helpers ─────────────────────────────────────────────────────────

const roundRectPath = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

const drawCover = (ctx, img, x, y, w, h) => {
  const scale = Math.max(w / img.width, h / img.height);
  const sw = w / scale;
  const sh = h / scale;
  ctx.drawImage(img, (img.width - sw) / 2, (img.height - sh) / 2, sw, sh, x, y, w, h);
};

const drawBackground = (ctx, W, H, wallpaper) => {
  if (wallpaper) {
    drawCover(ctx, wallpaper, 0, 0, W, H);
  } else {
    const g = ctx.createLinearGradient(0, 0, W, H);
    g.addColorStop(0, '#2a2723');
    g.addColorStop(1, '#0d0d0d');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
    const glow = ctx.createRadialGradient(W * 0.8, H * 0.15, 0, W * 0.8, H * 0.15, W);
    glow.addColorStop(0, 'rgba(255,189,89,0.35)');
    glow.addColorStop(1, 'rgba(255,189,89,0)');
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);
  }
  // Keeps white text readable on any wallpaper while letting the photo show:
  // a light overall dim, darker behind the text block (upper-middle) and
  // towards the bottom edge (the wordmark).
  ctx.fillStyle = 'rgba(0,0,0,0.18)';
  ctx.fillRect(0, 0, W, H);
  const band = ctx.createLinearGradient(0, 0, 0, H);
  band.addColorStop(0, 'rgba(0,0,0,0.35)');
  band.addColorStop(0.4, 'rgba(0,0,0,0.42)');
  band.addColorStop(0.68, 'rgba(0,0,0,0.08)');
  band.addColorStop(0.85, 'rgba(0,0,0,0.2)');
  band.addColorStop(1, 'rgba(0,0,0,0.6)');
  ctx.fillStyle = band;
  ctx.fillRect(0, 0, W, H);
};

const withShadow = (ctx, blur, offsetY, alpha, draw) => {
  ctx.save();
  ctx.shadowColor = `rgba(0,0,0,${alpha})`;
  ctx.shadowBlur = blur;
  ctx.shadowOffsetY = offsetY;
  draw();
  ctx.restore();
};

// The event's logo in a circle with a thin white ring.
const drawLogoCircle = (ctx, img, cx, cy, d) => {
  const r = d / 2;
  const ring = Math.max(4, Math.round(d / 32));
  withShadow(ctx, 30, 8, 0.35, () => {
    ctx.beginPath();
    ctx.arc(cx, cy, r + ring, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
  });
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.clip();
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(cx - r, cy - r, d, d);
  drawCover(ctx, img, cx - r, cy - r, d, d);
  ctx.restore();
};

// A map pin (teardrop with a hole), `h` tall, top-left at (x, y); returns
// its width.
const drawPin = (ctx, x, y, h) => {
  const r = h * 0.36;
  const cx = x + r;
  const cy = y + r;
  ctx.beginPath();
  ctx.moveTo(cx, y + h);
  ctx.bezierCurveTo(cx - r * 0.35, y + h - r * 0.9, cx - r, cy + r * 0.6, cx - r, cy);
  ctx.arc(cx, cy, r, Math.PI, 0);
  ctx.bezierCurveTo(cx + r, cy + r * 0.6, cx + r * 0.35, y + h - r * 0.9, cx, y + h);
  ctx.closePath();
  ctx.moveTo(cx + r * 0.42, cy);
  ctx.arc(cx, cy, r * 0.42, 0, Math.PI * 2, true);
  ctx.fill('evenodd');
  return r * 2;
};

// The yellow "book your space" button, centred on cx; returns its height.
const drawCta = (ctx, text, cx, top, size, maxWidth) => {
  let fontSize = size;
  const label = text.toUpperCase();
  const spacing = (s) => `${Math.round(s * 0.03)}px`;
  const measure = () => {
    ctx.font = font(800, fontSize);
    if ('letterSpacing' in ctx) ctx.letterSpacing = spacing(fontSize);
    return ctx.measureText(label).width + fontSize * 2.6;
  };
  let w = measure();
  while (w > maxWidth && fontSize > 20) {
    fontSize -= 2;
    w = measure();
  }
  const h = Math.round(size * 2.35);
  withShadow(ctx, 24, 6, 0.3, () => {
    roundRectPath(ctx, cx - w / 2, top, w, h, h / 2);
    ctx.fillStyle = BRAND;
    ctx.fill();
  });
  ctx.fillStyle = INK;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, cx, top + h / 2 + 1);
  if ('letterSpacing' in ctx) ctx.letterSpacing = '0px';
  return h;
};

const drawWordmark = (ctx, img, x, y, height) => {
  const w = (img.width / img.height) * height;
  ctx.drawImage(img, x, y, w, height);
  return w;
};

// ── layouts ─────────────────────────────────────────────────────────────────
//
// Both formats stack the same things, centred: event logo → event name →
// date → location (with a pin) → the "book your space" button; the there2b
// wordmark sits at the bottom centre.

const LAYOUTS = {
  story: {
    padX: 90,
    // Instagram covers the top ~220px (progress bar, account) and the bottom
    // ~250px (reply box) of a story.
    safeTop: 260,
    wordmarkH: 64,
    wordmarkBottom: 250,
    centerY: 0.42,
    logo: 190,
    gapAfterLogo: 56,
    maxTitle: 132,
    minTitle: 60,
    gapAfterTitle: 34,
    meta: 46,
    metaGap: 14,
    gapBeforeCta: 60,
    cta: 38,
  },
  post: {
    padX: 72,
    safeTop: 64,
    wordmarkH: 56,
    wordmarkBottom: 60,
    centerY: 0.45,
    logo: 150,
    gapAfterLogo: 40,
    maxTitle: 118,
    minTitle: 52,
    gapAfterTitle: 26,
    meta: 40,
    metaGap: 10,
    gapBeforeCta: 46,
    cta: 33,
  },
};

function drawLayout(ctx, { W, H, L, assets, content, cta }) {
  const cx = W / 2;
  const maxWidth = W - L.padX * 2;

  const wordmarkTop = H - L.wordmarkBottom - L.wordmarkH;
  if (assets.wordmark) {
    const w = (assets.wordmark.width / assets.wordmark.height) * L.wordmarkH;
    drawWordmark(ctx, assets.wordmark, cx - w / 2, wordmarkTop, L.wordmarkH);
  }

  // Everything but the title has a fixed height; the title gets the rest.
  const metaLine = Math.round(L.meta * 1.3);
  const metaLines = [content.date, content.location].filter(Boolean).length;
  const logoH = assets.logo ? L.logo + L.gapAfterLogo : 0;
  const metaH = metaLines ? L.gapAfterTitle + metaLines * metaLine + (metaLines - 1) * L.metaGap : 0;
  const ctaH = L.gapBeforeCta + Math.round(L.cta * 2.35);
  const top = L.safeTop;
  const bottom = wordmarkTop - 60;
  const title = fitText(ctx, content.name, {
    maxWidth,
    maxHeight: bottom - top - logoH - metaH - ctaH,
    maxSize: L.maxTitle,
    minSize: L.minTitle,
    maxLines: 4,
    weight: 800,
  });
  const titleH = title.lines.length * title.lineHeight;
  const groupH = logoH + titleH + metaH + ctaH;
  let y = Math.min(Math.max(H * L.centerY - groupH / 2, top), bottom - groupH);

  if (assets.logo) {
    drawLogoCircle(ctx, assets.logo, cx, y + L.logo / 2, L.logo);
    y += logoH;
  }

  ctx.textAlign = 'center';
  ctx.textBaseline = 'top';
  withShadow(ctx, 24, 4, 0.45, () => {
    ctx.fillStyle = '#ffffff';
    ctx.font = font(800, title.size);
    title.lines.forEach((line, i) => ctx.fillText(line, cx, y + i * title.lineHeight));
  });
  y += titleH;

  if (metaLines) {
    y += L.gapAfterTitle;
    withShadow(ctx, 16, 2, 0.45, () => {
      ctx.fillStyle = '#ffffff';
      if (content.date) {
        ctx.font = font(700, L.meta);
        ctx.textAlign = 'center';
        ctx.fillText(ellipsize(ctx, content.date, maxWidth), cx, y);
        y += metaLine + L.metaGap;
      }
      if (content.location) {
        ctx.font = font(600, L.meta);
        const pinH = Math.round(L.meta * 1.05);
        const gap = Math.round(L.meta * 0.3);
        const text = ellipsize(ctx, content.location, maxWidth - pinH - gap);
        const total = pinH * 0.72 + gap + ctx.measureText(text).width;
        const x0 = cx - total / 2;
        const pinW = drawPin(ctx, x0, y + (metaLine - pinH) / 2, pinH);
        ctx.textAlign = 'left';
        ctx.fillText(text, x0 + pinW + gap, y);
        y += metaLine + L.metaGap;
      }
    });
    y -= L.metaGap;
  }

  y += L.gapBeforeCta;
  drawCta(ctx, cta, cx, y, L.cta, maxWidth);
}

// What the "book your space" button says, per language.
export const SHARE_IMAGE_CTA = {
  pt: 'Reserve o seu espaço',
  en: 'Book your space',
};

// ── public API ──────────────────────────────────────────────────────────────

const loadImage = (src) =>
  new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });

export const shareMediaUrl = (event, kind) =>
  `${api.defaults.baseURL}/events/${encodeURIComponent(event.id)}/share-media/${kind}`;

/**
 * Everything the layouts draw that has to be fetched. Load once per event
 * and reuse for both formats.
 */
export async function loadShareAssets(event) {
  const fontsReady = document.fonts?.load
    ? Promise.all([
        document.fonts.load(font(800, 100)),
        document.fonts.load(font(700, 40)),
        document.fonts.load(font(600, 30)),
      ]).catch(() => {})
    : Promise.resolve();

  const [wallpaper, logo, wordmark] = await Promise.all([
    event.landscapeImage ? loadImage(shareMediaUrl(event, 'landscape')) : null,
    event.avatar ? loadImage(shareMediaUrl(event, 'avatar')) : null,
    loadImage(wordmarkSrc),
    fontsReady,
  ]);
  return { wallpaper, logo, wordmark };
}

/**
 * @param {object} args
 * @param {'story'|'post'} args.format
 * @param {object} args.event
 * @param {object} args.assets  from loadShareAssets
 * @param {string} args.lang    'pt' | 'en' — the button text and month names
 * @returns {HTMLCanvasElement}
 */
export function renderShareImage({ format, event, assets, lang = 'pt' }) {
  const { width: W, height: H } = SHARE_FORMATS[format];
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is not supported');

  drawBackground(ctx, W, H, assets.wallpaper);
  const content = {
    name: event.name || '',
    date: formatShareDate(event.date, lang),
    location: shareLocation(event),
  };
  drawLayout(ctx, {
    W,
    H,
    L: LAYOUTS[format],
    assets,
    content,
    cta: SHARE_IMAGE_CTA[lang] || SHARE_IMAGE_CTA.pt,
  });
  return canvas;
}

export const canvasToFile = (canvas, filename) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob
          ? resolve(new File([blob], filename, { type: 'image/jpeg' }))
          : reject(new Error('Could not export the image')),
      'image/jpeg',
      0.92
    );
  });
